import React, { useMemo, useState } from 'react'

import {
  Box,
  Button,
  Container,
  Grid,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core'
import { ArrowLeft } from 'react-feather'
import { isEmpty } from 'lodash'

import { LoadingFeedback } from 'components'
import ProfileForm from './components/ProfileForm'
import CertificateCard from './components/CertificateCard'
import ChipsFilter from 'views/Training/Home/components/ChipsFilter'

import useAuthTraining from 'hooks/useAuthTraining'
import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'
import helpers from 'helpers'
import * as service from 'service'
import { routes } from 'TrainingRoutes'
import useStyles from './styles'

const ALL_CHIP_ID = 'all'
const OTHERS_CHIP_ID = 'others'
const ORDER_OPTIONS = [
  { value: 'recent', label: 'Mais Recentes' },
  { value: 'oldest', label: 'Mais Antigos' },
]

const Profile = ({ match, history }) => {
  const classes = useStyles()
  const snackbar = useSnackbar()
  const { portalToken: contextPortalToken } = useAuthTraining()

  const portalToken = match?.params?.portalToken || contextPortalToken

  const [isSaving, setIsSaving] = useState(false)
  const [activeChip, setActiveChip] = useState(ALL_CHIP_ID)
  const [order, setOrder] = useState('recent')

  const activeCategoryId =
    activeChip === ALL_CHIP_ID || activeChip === OTHERS_CHIP_ID
      ? null
      : Number(activeChip)

  const {
    response: profileResponse,
    isLoading: isLoadingProfile,
    refresh: refreshProfile,
  } = useFetch(service.trainingPortal.profile.show)

  const { response: allCertsResponse, isLoading: isLoadingAllCerts } = useFetch(
    service.trainingPortal.certificates.index,
  )

  const { response: filteredResponse, isLoading: isLoadingFiltered } = useFetch(
    service.trainingPortal.certificates.index,
    { category_id: activeCategoryId },
    [activeCategoryId],
    activeCategoryId !== null,
  )

  const profile = profileResponse?.data?.profile || null
  const allCertificates = allCertsResponse?.data?.certificates || []
  const certificates = activeCategoryId
    ? filteredResponse?.data?.certificates || []
    : allCertificates

  const handleSave = async (data) => {
    setIsSaving(true)
    try {
      await service.trainingPortal.profile.update(data)
      refreshProfile()
      snackbar.open({
        message: 'Perfil atualizado com sucesso.',
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao salvar o perfil.',
        variant: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const sortedCategories = useMemo(() => {
    const categoryMap = {}
    allCertificates.forEach((certificate) => {
      const categories = certificate.categories || []
      categories.forEach((category) => {
        if (!categoryMap[category.id]) {
          categoryMap[category.id] = {
            id: String(category.id),
            label: category.name,
            count: 0,
          }
        }
        categoryMap[category.id].count += 1
      })
    })
    return Object.values(categoryMap).sort((a, b) => b.count - a.count)
  }, [allCertificates])

  const chips = useMemo(
    () => [
      { id: ALL_CHIP_ID, label: `Todos(${allCertificates.length})` },
      ...sortedCategories.slice(0, 3).map((category) => ({
        id: category.id,
        label: `${category.label}(${category.count})`,
      })),
      ...(sortedCategories.length > 3
        ? [{ id: OTHERS_CHIP_ID, label: 'Outros' }]
        : []),
    ],
    [sortedCategories, allCertificates.length],
  )

  const otherCategories = useMemo(
    () =>
      sortedCategories.slice(3).map((category) => ({
        id: category.id,
        label: `${category.label}(${category.count})`,
      })),
    [sortedCategories],
  )

  const sortedCertificates = useMemo(
    () =>
      [...certificates].sort((a, b) => {
        const dateA = a.completedAt ? new Date(a.completedAt) : new Date(0)
        const dateB = b.completedAt ? new Date(b.completedAt) : new Date(0)
        return order === 'recent' ? dateB - dateA : dateA - dateB
      }),
    [certificates, order],
  )

  if (isLoadingProfile || isLoadingAllCerts) return <LoadingFeedback open />

  return (
    <Container maxWidth>
      <Box className={classes.root}>
        <Button
          startIcon={<ArrowLeft size={18} />}
          className={classes.backButton}
          onClick={() =>
            history.push(routes.home.replace(':portalToken', portalToken))
          }
        >
          Voltar ao início
        </Button>

        <ProfileForm
          profile={profile}
          onSave={handleSave}
          isSaving={isSaving}
        />

        <Box className={classes.certificatesHeader}>
          <Typography className={classes.certificatesTitle}>
            Certificados
          </Typography>
        </Box>

        <Box className={classes.filterRow}>
          {!isEmpty(chips) && (
            <ChipsFilter
              chips={chips}
              activeChip={activeChip}
              onChipChange={setActiveChip}
              otherCategories={otherCategories}
            />
          )}
          <Box className={classes.sortRow}>
            <Typography className={classes.sortLabel}>Ordenar por:</Typography>
            <Select
              value={order}
              onChange={(event) => setOrder(event.target.value)}
              variant="outlined"
              className={classes.sortSelect}
            >
              {ORDER_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        <LoadingFeedback open={isLoadingFiltered} />

        {!isLoadingFiltered && isEmpty(sortedCertificates) ? (
          <Box className={classes.emptyState}>
            <Typography className={classes.emptyText}>
              Nenhum certificado encontrado.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {sortedCertificates.map((certificate) => (
              <Grid key={certificate.courseId} item xs={12} sm={6} md={4}>
                <CertificateCard certificate={certificate} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Container>
  )
}

export default Profile
